import { useSelector } from "react-redux";
import { Typography, Grid, FormControlLabel, Checkbox, FormHelperText, TextField } from "@mui/material";
import { useForm, Controller } from "react-hook-form"
import { z } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import { isValidIBAN } from 'ibantools';
import { useTranslation } from "react-i18next";
import { IStepForm, IStepPaymentFormData } from "../types/stepsFormData";
import { FC, useContext, useEffect } from "react";
import { RootState } from "@/store";
import { SubmitButtonContext } from "../Contractation";

const FormPayment: FC<IStepForm<IStepPaymentFormData>> = ({
  type,
  onSubmit
}) => {
  const submitButtonContext = useContext(SubmitButtonContext);

  const { t } = useTranslation();
  const contractation = useSelector((state: RootState) => state[`${type}Contractation`]);

  const onPaymentSubmit = async (values: IStepPaymentFormData) => {
    await onSubmit(values)
  };
  
  const schema = z.object({
    iban: z.string().min(1, {message: t('common:text.required_field')})
    .refine((val) => isValidIBAN(val.replaceAll(' ','')), {
      message: t('common:text.iban_field_invalid')
    }),
    isOwner: z.boolean().refine((val) => val === true, {
      message: t('common:text.required_field'),
    })
  });

  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isSubmitting }
  } = useForm<IStepPaymentFormData>({
    resolver: zodResolver(schema),
    defaultValues: contractation.steps.payment
  });

  useEffect(() => {
    submitButtonContext?.setValues({
      onClick: () => handleSubmit(onPaymentSubmit)(),
      loading: isSubmitting,
    });
  }, [isSubmitting, handleSubmit]);

  return (
    <form onSubmit={handleSubmit(onPaymentSubmit)}>
      <Grid container sx={{mr: {md: 5}, ml: {md: 5}}}>

        <Grid item xs={12}>
          <Typography variant="h6">
            {t('common:text.contractation_iban_full_name')}
          </Typography>

          <Grid item xs={12} md={6}>
            <TextField
              {...register("iban")}
              label={t('common:text.iban_field_label')}
              placeholder={t('common:text.iban_field_placeholder')}
              sx={{width: "100%"}}
              variant="standard"
              error={!!errors.iban}
            />
            {errors.iban?.message && 
              <FormHelperText error={true} id="iban-field-error-text">
                {errors.iban?.message}
              </FormHelperText>
            }
          </Grid>

        </Grid>

        <Grid item xs={12}>

          <Controller
            name={"isOwner"}
            control={control}
            render={({ field }) => <FormControlLabel
              control={<Checkbox
                {...field}
                checked={field.value}
                />} 
              label={
                <Typography variant="body1" color="black">
                  {t('common:text.contractation_owner_confirmation')}
                </Typography>
              } 
            />}
          />
          {errors.isOwner?.message &&
            <FormHelperText error={true}>
              {errors.isOwner.message}
            </FormHelperText>
          }
        </Grid>
      </Grid>

    </form>
  );
};

export default FormPayment;
